import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AssetHistoryCheck.css';

const AssetHistoryCheck = () => {
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [assets, setAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [period, setPeriod] = useState(null);
  const [onlyOutdated, setOnlyOutdated] = useState(true);
  const [minMissingDays, setMinMissingDays] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [limitOptions] = useState([10, 50, 200, 500]);

  const { token, logout } = useAuth();

  const loadData = async (page = 1, limit = pagination.limit, filterOutdated = onlyOutdated, minMissing = minMissingDays, sort = null, sortDir = 'asc') => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        only_outdated: filterOutdated.toString(),
      });
      
      if (minMissing > 0) {
        params.append('min_missing_days', minMissing.toString());
      }
      
      // Utiliser les paramètres de tri passés en argument, sinon utiliser l'état
      const currentSortField = sort !== null ? sort : sortField;
      const currentSortDirection = sort !== null ? sortDir : sortDirection;
      
      // Ajouter les paramètres de tri
      if (currentSortField) {
        params.append('sort_field', currentSortField);
        params.append('sort_direction', currentSortDirection);
      }

      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`/api/alpaca/history/check?${params.toString()}`, { headers });

      if (response.status === 401) {
        let msg = 'Token invalide ou expiré';
        try {
          const j = await response.json();
          msg = j.message || msg;
        } catch {}
        setError(msg);
        await logout();
        return;
      }

      if (!response.ok) {
        let msg = 'Erreur lors du chargement des assets';
        try {
          const j = await response.json();
          msg = j.error || j.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      console.log('[AssetHistoryCheck] Full API Response:', JSON.stringify(data, null, 2)); // Debug
      console.log('[AssetHistoryCheck] loadData - sort params received:', { sort, sortDir, currentSortField, currentSortDirection });
      if (data.success) {
        const assetsList = Array.isArray(data.assets) ? data.assets : [];
        console.log('[AssetHistoryCheck] Assets received:', assetsList.length); // Debug
        console.log('[AssetHistoryCheck] Pagination info:', data.pagination); // Debug
        if (data.pagination) {
          console.log('[AssetHistoryCheck] Pagination details:', {
            page: data.pagination.page,
            limit: data.pagination.limit,
            total: data.pagination.total,
            pages: data.pagination.pages,
            calculated_pages: data.pagination.total > 0 ? Math.ceil(data.pagination.total / data.pagination.limit) : 1
          });
        }
        console.log('[AssetHistoryCheck] Period info:', data.period); // Debug
        console.log('[AssetHistoryCheck] Debug info:', data.debug); // Debug
        
        if (assetsList.length === 0 && data.pagination && data.pagination.total > 0) {
          console.warn('[AssetHistoryCheck] WARNING: No assets in array but total > 0. This might be a bug.');
        }
        
        // Debug: vérifier l'ordre des assets reçus
        if (assetsList.length > 0) {
          const first5Missing = assetsList.slice(0, 5).map(a => ({
            symbol: a.symbol,
            missing: a.history_status?.missing ?? 0,
            found: a.history_status?.found ?? 0,
            expected: a.history_status?.expected ?? 0
          }));
          console.log('[AssetHistoryCheck] First 5 assets received:', first5Missing);
          console.log('[AssetHistoryCheck] Sort field used:', currentSortField, 'Sort direction:', currentSortDirection);
        }
        
        // Filtrer les assets sans symbole valide avant de les définir
        const validAssets = assetsList.filter(asset => {
          const hasValidSymbol = asset?.symbol && typeof asset.symbol === 'string' && asset.symbol.trim().length > 0;
          if (!hasValidSymbol) {
            console.warn('[AssetHistoryCheck] Asset sans symbole valide ignoré:', {
              asset,
              symbol: asset?.symbol,
              symbolType: typeof asset?.symbol,
              hasSymbol: 'symbol' in (asset || {})
            });
          }
          return hasValidSymbol;
        });
        
        if (validAssets.length !== assetsList.length) {
          console.warn(`[AssetHistoryCheck] ${assetsList.length - validAssets.length} asset(s) sans symbole valide filtré(s) sur ${assetsList.length} total`);
        }
        
        // Vérifier que tous les assets filtrés ont bien un symbole valide
        const invalidInFiltered = validAssets.filter(a => !a?.symbol || typeof a.symbol !== 'string' || !a.symbol.trim());
        if (invalidInFiltered.length > 0) {
          console.error('[AssetHistoryCheck] ERREUR: Des assets invalides sont passés dans le filtre!', invalidInFiltered);
        }
        
        setAssets(validAssets);
        
        // Vérifier que la pagination est bien définie
        if (data.pagination) {
          console.log('[AssetHistoryCheck] Setting pagination:', data.pagination);
          console.log('[AssetHistoryCheck] Pages value:', data.pagination.pages, 'Type:', typeof data.pagination.pages);
          
          const newPagination = {
            page: data.pagination.page ?? 1,
            limit: data.pagination.limit ?? 50,
            total: data.pagination.total ?? 0,
            pages: data.pagination.pages ?? 0
          };
          
          console.log('[AssetHistoryCheck] New pagination state:', newPagination);
          setPagination(newPagination);
        } else {
          console.warn('[AssetHistoryCheck] WARNING: No pagination in response!');
          setPagination(pagination); // Garder l'état actuel
        }
        
        setPeriod(data.period || null);
      } else {
        throw new Error(data.error || data.message || 'Erreur inconnue');
      }
    } catch (e) {
      setError(e.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Chargement initial seulement - ne pas recharger quand les autres états changent
    loadData(1, pagination.limit, onlyOutdated, minMissingDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    loadData(1, newLimit, onlyOutdated, minMissingDays);
  };

  const handleOnlyOutdatedChange = (checked) => {
    setOnlyOutdated(checked);
    setSelectedAssets(new Set()); // Réinitialiser la sélection
    loadData(1, pagination.limit, checked, minMissingDays);
  };

  const handleMinMissingDaysChange = (value) => {
    const days = parseInt(value) || 0;
    setMinMissingDays(days);
    setSelectedAssets(new Set()); // Réinitialiser la sélection
    loadData(1, pagination.limit, onlyOutdated, days);
  };

  const handlePageChange = (newPage) => {
    // Transmettre les paramètres de tri lors du changement de page
    loadData(newPage, pagination.limit, onlyOutdated, minMissingDays, sortField, sortDirection);
  };

  const handleSort = (field) => {
    // Si on clique sur le même champ que celui déjà trié, inverser la direction
    // Sinon, commencer par 'asc'
    let newDirection = 'asc';
    if (sortField === field && sortDirection === 'asc') {
      newDirection = 'desc';
    }
    
    console.log('[AssetHistoryCheck] handleSort - field:', field, 'current sortField:', sortField, 'newDirection:', newDirection);
    
    // Recharger IMMÉDIATEMENT avec les nouvelles valeurs de tri (avant de mettre à jour l'état)
    // Cela évite les problèmes de timing avec React
    loadData(1, pagination.limit, onlyOutdated, minMissingDays, field, newDirection);
    
    // Mettre à jour l'état après pour que l'interface affiche correctement le tri actif
    setSortField(field);
    setSortDirection(newDirection);
  };

  const handleSelectAsset = (symbol) => {
    // Ignorer si le symbole est invalide
    if (!symbol || typeof symbol !== 'string' || !symbol.trim()) {
      console.warn('[AssetHistoryCheck] Tentative de sélection d\'un symbole invalide:', symbol);
      return;
    }
    const newSelected = new Set(selectedAssets);
    if (newSelected.has(symbol)) {
      newSelected.delete(symbol);
    } else {
      newSelected.add(symbol);
    }
    setSelectedAssets(newSelected);
  };

  const handleSelectAll = () => {
    // Filtrer d'abord les assets valides
    const validAssets = assets.filter(a => a?.symbol && typeof a.symbol === 'string' && a.symbol.trim().length > 0);
    const validSymbols = validAssets.map(a => a.symbol.trim());
    
    if (selectedAssets.size === validSymbols.length && validSymbols.length > 0) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(validSymbols));
    }
  };

  const handleUpdateSingle = async (symbol) => {
    await handleUpdateBatch([symbol]);
  };

  const handleUpdateBatch = async (symbolsToUpdate = null) => {
    let symbols = symbolsToUpdate || Array.from(selectedAssets);
    
    // Filtrer les symboles invalides (undefined, null, chaînes vides)
    symbols = symbols.filter(s => s && typeof s === 'string' && s.trim().length > 0);
    
    // Filtrer les symboles avec "/" (cryptos comme AAVE/USD) - Alpaca utilise un format différent pour les cryptos
    const beforeCryptoFilter = symbols.length;
    symbols = symbols.filter(s => {
      const trimmed = s.trim().toUpperCase();
      return !trimmed.includes('/');
    });
    if (beforeCryptoFilter > symbols.length) {
      console.log(`[AssetHistoryCheck] ${beforeCryptoFilter - symbols.length} symbole(s) avec "/" exclu(s) (cryptos non supportées pour les barres quotidiennes)`);
    }
    
    // Filtrer les symboles avec suffixes invalides (_DELISTED, etc.)
    const beforeDelistedFilter = symbols.length;
    symbols = symbols.filter(s => {
      const trimmed = s.trim().toUpperCase();
      return !trimmed.endsWith('_DELISTED') && 
             !trimmed.endsWith('_OLD') && 
             !trimmed.endsWith('_WITHDRWN') && 
             !trimmed.endsWith('_SUSPENDED') &&
             !trimmed.includes('_DELISTED');
    });
    if (beforeDelistedFilter > symbols.length) {
      console.log(`[AssetHistoryCheck] ${beforeDelistedFilter - symbols.length} symbole(s) avec suffixe invalide exclu(s)`);
    }
    
    if (symbols.length === 0) {
      setError('Aucun symbole valide sélectionné après filtrage (symboles avec "/" ou suffixes invalides exclus)');
      return;
    }
    
    // Vérifier s'il y a des symboles invalides avant l'envoi
    const invalidSymbols = symbols.filter(s => !s || typeof s !== 'string' || !s.trim());
    if (invalidSymbols.length > 0) {
      console.error('[AssetHistoryCheck] Symboles invalides détectés avant envoi:', invalidSymbols);
      setError(`Erreur: ${invalidSymbols.length} symbole(s) invalide(s) détecté(s) avant l'envoi. Veuillez recharger la page.`);
      return;
    }

    console.log('[AssetHistoryCheck] Updating symbols:', symbols);
    console.log('[AssetHistoryCheck] Symbols count:', symbols.length);
    console.log('[AssetHistoryCheck] Symbols types:', symbols.map(s => ({ symbol: s, type: typeof s, length: s?.length })));

    try {
      setUpdating(true);
      setError(null);

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch('/api/alpaca/history/update', {
        method: 'POST',
        headers,
        body: JSON.stringify({ symbols })
      });

      if (response.status === 401) {
        await logout();
        return;
      }

      const data = await response.json();
      
      // Debug: afficher la réponse complète dans la console
      console.log('[AssetHistoryCheck] Update response:', JSON.stringify(data, null, 2));
      
      // Si la réponse HTTP n'est pas OK, lancer une erreur
      if (!response.ok) {
        let msg = 'Erreur lors de la mise à jour';
        msg = data.error || data.message || msg;
        throw new Error(msg);
      }
      
      // Si la réponse est OK mais success: false, on continue quand même
      // car le worker peut avoir traité certains symboles avec succès
      
      // Réinitialiser la pagination à la page 1 après mise à jour pour voir les changements
      setPagination(prev => ({ ...prev, page: 1 }));
      // Recharger les données après la mise à jour
      await loadData(1, pagination.limit, onlyOutdated, minMissingDays);
      setSelectedAssets(new Set());
      
      // Construire un message détaillé
      const inserted = data.inserted || 0;
      const skipped = data.skipped || 0;
      const details = data.details || {};
      const results = data.results || {};
      
      // Debug: afficher les détails extraits
      console.log('[AssetHistoryCheck] Extracted details:', { inserted, skipped, details, results });
      
      let msg = '';
      const successful = details.successful || 0;
      const failed = details.failed || 0;
      const skippedCount = details.skipped || 0;
      
      if (successful > 0 && failed === 0 && skippedCount === 0) {
        // Tout s'est bien passé
        msg = `✅ Mise à jour réussie pour ${symbols.length} symbole(s).\n`;
        if (inserted > 0) msg += `✅ ${inserted} nouvelle(s) barre(s) insérée(s)\n`;
        if (skipped > 0) msg += `⏭️ ${skipped} barre(s) existante(s) ignorée(s)\n`;
      } else {
        // Il y a eu des erreurs ou des symboles ignorés
        msg = `⚠️ Mise à jour partielle :\n`;
        if (successful > 0) msg += `✅ ${successful} symbole(s) mis à jour avec succès\n`;
        if (failed > 0) msg += `❌ ${failed} symbole(s) ont échoué\n`;
        if (skippedCount > 0) msg += `⏭️ ${skippedCount} symbole(s) ignorés\n`;
        if (inserted > 0) msg += `📊 ${inserted} nouvelle(s) barre(s) insérée(s)\n`;
        if (skipped > 0) msg += `📊 ${skipped} barre(s) existante(s) ignorée(s)\n`;
        
        // Afficher les détails des échecs
        if (results.failed && results.failed.length > 0) {
          msg += `\n❌ Symboles en échec :\n`;
          results.failed.slice(0, 10).forEach(r => {
            const symbol = r.symbol || 'Symbole inconnu';
            const error = r.error || 'Erreur inconnue';
            msg += `  - ${symbol}: ${error}\n`;
          });
          if (results.failed.length > 10) {
            msg += `  ... et ${results.failed.length - 10} autre(s)\n`;
          }
        }
        
        // Afficher les détails des symboles ignorés
        if (results.skipped && results.skipped.length > 0) {
          msg += `\n⏭️ Symboles ignorés :\n`;
          results.skipped.slice(0, 10).forEach(r => {
            msg += `  - ${r.symbol}: ${r.reason || 'Raison inconnue'}\n`;
          });
          if (results.skipped.length > 10) {
            msg += `  ... et ${results.skipped.length - 10} autre(s)\n`;
          }
        }
      }
      
      // Afficher le message (informatif, pas une erreur)
      alert(msg);
      
      // Ne pas lancer d'erreur - les détails ont déjà été affichés dans l'alert
      // Les échecs sont normaux pour certains symboles (symboles invalides, etc.)
    } catch (e) {
      setError(e.message || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status.is_up_to_date) {
      return <span className="badge badge-success">À jour</span>;
    }
    return (
      <span className="badge badge-warning">
        {status.missing} jour{status.missing > 1 ? 's' : ''} manquant{status.missing > 1 ? 's' : ''}
      </span>
    );
  };

  return (
    <div className="asset-history-check">
      <div className="page-header">
        <h1>Vérification de l'historique des Assets</h1>
        <p className="page-description">
          Vérification de l'historique sur 30 jours ouvrés pour tous les assets actifs et tradables
        </p>
      </div>

      {period && (
        <div className="period-info">
          <strong>Période analysée :</strong> {period.start} au {period.end}
          {' '}({period.expected_trading_days} jours ouvrés attendus)
        </div>
      )}

      <div className="filters-bar">
        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={onlyOutdated}
              onChange={(e) => handleOnlyOutdatedChange(e.target.checked)}
            />
            Afficher seulement les assets non à jour
          </label>
        </div>

        {onlyOutdated && (
          <div className="filter-group">
            <label>Jours manquants minimum :</label>
            <input
              type="number"
              min="0"
              max={period?.expected_trading_days || 30}
              value={minMissingDays}
              onChange={(e) => handleMinMissingDaysChange(e.target.value)}
              style={{ width: '80px', padding: '4px', marginLeft: '8px' }}
            />
            <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#666' }}>
              (0 = tous les non à jour)
            </span>
          </div>
        )}

        <div className="filter-group">
          <label>Éléments par page :</label>
          <select
            value={pagination.limit}
            onChange={(e) => handleLimitChange(parseInt(e.target.value))}
          >
            {limitOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        <div className="batch-actions">
          <button
            className="btn btn-primary"
            onClick={() => handleUpdateBatch()}
            disabled={selectedAssets.size === 0 || updating}
          >
            {updating ? 'Mise à jour...' : `Mettre à jour ${selectedAssets.size} sélectionné(s)`}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des assets...</p>
        </div>
      ) : (
        <>
          {error && (
            <div className="alert alert-error" style={{marginBottom: '20px'}}>
              <strong>Erreur :</strong> {error}
            </div>
          )}
          
          {!error && pagination.total === 0 && (
            <div className="alert alert-warning" style={{marginBottom: '20px'}}>
              {onlyOutdated 
                ? 'Tous les assets de la page courante sont à jour !' 
                : 'Aucun asset trouvé. Vérifiez les filtres.'}
            </div>
          )}
          
          {!error && pagination.total > 0 && (
            <div className="info-banner" style={{marginBottom: '20px', padding: '12px', background: '#e7f3ff', borderRadius: '4px'}}>
              <strong>{assets.length}</strong> asset(s) affiché(s) sur cette page (sur {pagination.total} total) - Page {pagination.page} sur {pagination.pages || '?'} 
              <span style={{fontSize: '0.8em', color: '#666', marginLeft: '10px'}}>
                {onlyOutdated && `(Filtre: seulement non à jour)`}
              </span>
            </div>
          )}
          
          <div className="table-container">
            <table className="assets-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={assets.length > 0 && assets.filter(a => a?.symbol && typeof a.symbol === 'string' && a.symbol.trim().length > 0).every(a => selectedAssets.has(a.symbol.trim()))}
                      onChange={handleSelectAll}
                      disabled={updating}
                    />
                  </th>
                  <th>Symbole</th>
                  <th>Nom</th>
                  <th>Status</th>
                  <th>
                    <button
                      onClick={() => handleSort('found')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      Jours trouvés
                      {sortField === 'found' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                    </button>
                  </th>
                  <th>
                    <button
                      onClick={() => handleSort('expected')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      Jours attendus
                      {sortField === 'expected' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                    </button>
                  </th>
                  <th>
                    <button
                      onClick={() => handleSort('missing')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      Jours manquants
                      {sortField === 'missing' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                    </button>
                  </th>
                  <th>
                    <button
                      onClick={() => handleSort('last_date')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      Dernière date
                      {sortField === 'last_date' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                    </button>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-state">
                      {onlyOutdated 
                        ? 'Tous les assets sont à jour !' 
                        : 'Aucun asset trouvé'}
                    </td>
                  </tr>
                ) : (
                  assets
                    .filter(asset => asset?.symbol && typeof asset.symbol === 'string' && asset.symbol.trim().length > 0)
                    .map((asset) => {
                      const status = asset.history_status || {};
                      const symbol = asset.symbol.trim();
                      const isSelected = selectedAssets.has(symbol);
                      
                      return (
                        <tr key={symbol} className={status.is_up_to_date ? 'up-to-date' : 'outdated'}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectAsset(symbol)}
                              disabled={updating}
                            />
                          </td>
                          <td className="symbol-cell">{symbol}</td>
                        <td>{asset.name || '-'}</td>
                        <td>{getStatusBadge(status)}</td>
                        <td>{status.found || 0}</td>
                        <td>{status.expected || 0}</td>
                        <td>{status.missing || 0}</td>
                        <td>{status.last_date || 'Aucune'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleUpdateSingle(symbol)}
                            disabled={updating || status.is_up_to_date}
                          >
                            {updating ? '...' : 'Mettre à jour'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1 || updating}
              >
                Précédent
              </button>
              <span className="page-info">
                Page {pagination.page} sur {pagination.pages} ({pagination.total} total)
              </span>
              <button
                className="btn btn-sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages || updating}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AssetHistoryCheck;

